import * as Enums from '@playbook-technologies/common-enums';

import { INFLUENCER_TIERS } from '@client/pages/admin/InfluencerList.store';
import { CircularProgress, Menu, MenuItem, Tooltip } from '@mui/material';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { colors } from '../colors';
import { bgColorsVars } from '@design-system/vars.css';

export const tierTooltipText: Record<number, string> = {
  1: 'Legacy creators that received “free” onboarding, design/programming services and/or custom landing page',
  2: 'Creators that paid for coaching services + receive help with onboarding, design/programming services and/or custom landing page',
  3: 'Creators that paid for coaching services only',
};

type Option = { name: string; value: number };

const options: Array<Option> = Object.values(Enums.Influencer.Tier).filter(
  (v) => !!v.value,
);

export const Tier = ({
  value,
  onChange,
  loading,
}: {
  value?: number;
  onChange: (value: number) => Promise<void>;
  loading?: boolean;
}) => {
  const [tier, setTier] = useState(value);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openSelection = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const close = () => {
    setAnchorEl(null);
  };

  const select = (selectedValue: number) => {
    if (selectedValue !== tier) {
      onChange(selectedValue).then(() => setTier(selectedValue));
    }
    close();
  };

  return (
    <div>
      <div
        data-testid="tier-value"
        className={classNames(
          'max-w-max rounded-[10px] min-w-[70px] font-bold hover:bg-[#EEE] active:bg-[#EEE] px-3.5 py-1.5 flex items-center gap-2',
          {
            'bg-[#EEE]': !!anchorEl,
            'pointer-events-none': loading,
            'min-w-full min-h-full': !value,
          },
        )}
        onClick={openSelection}
      >
        {loading ? (
          <div className="w-full text-center">
            <CircularProgress size={20} />
          </div>
        ) : (
          <>
            {tier ? `Tier ${INFLUENCER_TIERS[tier]?.name}` : ''}
            <FontAwesomeIcon
              data-testid="tier-select-arrow"
              icon={faCaretDown}
              className={classNames('cursor-pointer', {
                'inline-flex': !!anchorEl,
                'hidden group-hover:inline-flex': !anchorEl,
              })}
            />
          </>
        )}
      </div>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={close}
        data-testid="tier-select-menu"
      >
        <div>
          {options.map((option) => (
            <Tooltip
              title={tierTooltipText[option.value] || ''}
              key={option.name}
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: bgColorsVars['N-40'],
                    color: 'black',
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    padding: '4px 8px',
                  },
                },
              }}
            >
              <MenuItem
                onClick={() => select(option.value)}
                data-testid={`tier-option-${option.value}`}
                selected={option.value === tier}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: colors.neutral40,
                  },
                }}
              >
                {option.name}
              </MenuItem>
            </Tooltip>
          ))}
        </div>
      </Menu>
    </div>
  );
};
